import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Icons } from "../../Everou/_assets";
import { CustomModal } from "../../Everou/_components/CustomModal";
import { CapsuleButton } from "./CapsuleButton";
import { WaitSpinner } from "../../Everou/_components/WaitSpinner";
import { GeocodingHelper } from "../_helpers/GeocodingHelper";

// Where the map opens when the location has no coordinates yet: wide enough to
// place any of them, so the first click is never a long pan away.
const DEFAULT_VIEW = { latitude: 40.4168, longitude: -3.7038, zoom: 4 };
const PICKED_ZOOM = 16;
// Location.address in the backend.
const ADDRESS_MAX_LENGTH = 255;
// The backend stores degrees; 6 decimals is ~10 cm, well past what the guest
// position check can tell apart, and keeps the saved value short.
const DECIMALS = 6;

function round(value) {
    return Number(Number(value).toFixed(DECIMALS));
}

function formatCoordinates(latitude, longitude) {
    return `${round(latitude)}, ${round(longitude)}`;
}

export function LocationCoordinatesInput({ latitude, longitude, address, onSave }) {

    // LIBRARIES
    const { t } = useTranslation();

    // LOCAL STATE
    const [isEditing, setIsEditing] = useState(false);

    const hasCoordinates = latitude != null && longitude != null;

    return (
        <div className="p-1">
            <div className="d-flex align-items-center justify-content-between">
                <div className="flex-shrink-0 mr-3">
                    {t("location_coordinates_title")}
                </div>
                {/* One line, not a stack: an address runs long and piling the
                    coordinates under it crams the row. The address names the
                    place, so it is what shows; the coordinates take its place
                    when there is none, and both are always in the dialog. */}
                <div
                    className="d-flex align-items-center justify-content-end"
                    style={{ minWidth: 0, flex: "1 1 auto" }}
                >
                    <span
                        className={`text-truncate ${hasCoordinates && !address ? "text-monospace" : ""}`}
                        title={hasCoordinates && address ? address : undefined}
                    >
                        {!hasCoordinates
                            ? <span className="text-secondary">
                                {t("location_coordinates_not_set")}
                            </span>
                            : address || formatCoordinates(latitude, longitude)
                        }
                    </span>
                    <span
                        className="cp c-text-primary c-font-weight-medium ml-3 flex-shrink-0"
                        onClick={() => setIsEditing(true)}
                    >
                        {hasCoordinates ? t("global_edit") : t("global_add")}
                    </span>
                </div>
            </div>
            {isEditing &&
                <CoordinatesModal
                    latitude={latitude}
                    longitude={longitude}
                    address={address}
                    onSave={onSave}
                    onClose={() => setIsEditing(false)}
                />
            }
        </div>
    );
}

function CoordinatesModal({ latitude, longitude, address, onSave, onClose }) {

    // LIBRARIES
    const { t, i18n } = useTranslation();

    // LOCAL STATE
    // Empty, not prefilled with the saved address: typing into a box that already
    // holds a long address appends to it, and the search goes out as the two
    // glued together. The saved value is right below, in the address field.
    const [query, setQuery] = useState("");
    const [results, setResults] = useState(null); // null = no search done yet
    const [isSearching, setIsSearching] = useState(false);
    // Picking a suggestion is a second round trip with Places (its coordinates
    // only arrive with the place details), so the spinner covers that too.
    const [isResolving, setIsResolving] = useState(false);
    const [searchError, setSearchError] = useState(null);
    // The address rides along with the point, but it is the host's to write: OSM
    // has no house numbers on plenty of streets, so the geocoder answers with the
    // road and its label carries no number. What is searched sets the point; what
    // is typed here is what gets saved and what the guest is sent to.
    const [picked, setPicked] = useState(
        latitude != null && longitude != null
            ? { latitude, longitude, address: address || "" }
            : null,
    );

    const isEdited = picked && (
        latitude == null
        || longitude == null
        || round(picked.latitude) !== round(latitude)
        || round(picked.longitude) !== round(longitude)
        || picked.address !== (address || "")
    );

    // ACTION HANDLERS
    async function handleSearch() {
        const text = query.trim();
        if (!text || isSearching)
            return;

        // A pasted coordinate pair is already the answer — no geocoder needed. It
        // says nothing about the address, so whatever is written stays.
        const parsed = GeocodingHelper.parseCoordinates(text);
        if (parsed) {
            setResults(null);
            setSearchError(null);
            setPicked(previous => ({
                ...parsed,
                address: previous?.address || "",
                focus: true,
            }));
            return;
        }

        setIsSearching(true);
        setSearchError(null);
        try {
            const found = await GeocodingHelper.searchAddress(text, i18n.language);
            setResults(found);
            // A single match is unambiguous: place it without an extra click.
            if (found.length === 1)
                await pickResult(found[0]);
        } catch (error) {
            // The dialog only says "could not search": whatever Google refused —
            // key, referrer, API not enabled — is a deployment problem, and its
            // message is the only way to tell which.
            console.error("Address search failed", error);
            setResults(null);
            setSearchError(t("location_coordinates_search_error"));
        } finally {
            setIsSearching(false);
        }
    }

    // The searched text wins over the geocoder's label: when the street has no
    // house numbers the label comes back as the bare road, dropping the number
    // that was typed — and the number is the whole point of the address. The
    // label only fills in when nothing was typed to begin with.
    // `focus` asks the map to fly to it — a searched point has to be seen close
    // up to be checked, while a point clicked on the map is already in view.
    async function pickResult(result) {
        setIsResolving(true);
        setSearchError(null);
        try {
            const point = await result.resolve();
            setPicked({
                latitude: point.latitude,
                longitude: point.longitude,
                address: (query.trim() || point.label).slice(0, ADDRESS_MAX_LENGTH),
                focus: true,
                // Carried so the dialog can warn: the point of a street match is
                // the middle of the way, nowhere near the door on a long street.
                isStreet: result.isStreet,
            });
            // The choice is made: the list has done its job and now only hides
            // the map, which is where the point is checked. It stays up if the
            // pick failed, so another one can be tried.
            setResults(null);
        } catch (error) {
            setSearchError(t("location_coordinates_search_error"));
        } finally {
            setIsResolving(false);
        }
    }

    function handleSave() {
        onSave(round(picked.latitude), round(picked.longitude), picked.address);
        onClose();
    }

    return (
        <CustomModal
            show={true}
            centered
            size="lg"
            onHide={onClose}
            title={t("location_coordinates_modal_title")}
            content={
                <div>
                    <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-3">
                        <img
                            src={Icons.info}
                            alt="info"
                            className="mr-2 flex-shrink-0"
                            style={{ width: "24px", height: "24px" }}
                        />
                        <span>{t("location_coordinates_help")}</span>
                    </div>

                    <div className="d-flex align-items-center mb-3" style={{ columnGap: "8px" }}>
                        <input
                            className="w-100 px-2 py-1 rounded border"
                            style={{ flex: "1 1 0", minWidth: 0 }}
                            value={query}
                            placeholder={t("location_coordinates_search_placeholder")}
                            onChange={event => setQuery(event.target.value)}
                            onKeyDown={event => {
                                if (event.key === "Enter") {
                                    event.preventDefault();
                                    handleSearch();
                                }
                            }}
                        />
                        <CapsuleButton
                            text={t("location_coordinates_search_button")}
                            icon={Icons.search}
                            onClick={handleSearch}
                            isEnabled={!isSearching && !!query.trim()}
                            size={"SMALL"}
                        />
                    </div>

                    {(isSearching || isResolving) &&
                        <div className="py-2 d-flex justify-content-center">
                            <WaitSpinner />
                        </div>
                    }

                    {searchError &&
                        <div className="text-danger mb-3">{searchError}</div>
                    }

                    {!isSearching && !isResolving && results !== null && results.length === 0 &&
                        <div className="text-secondary mb-3">
                            {t("location_coordinates_no_results")}
                        </div>
                    }

                    {!isSearching && !isResolving && results !== null && results.length > 1 &&
                        <div className="border c-rounded-big bg-white mb-3">
                            {results.map(result => (
                                <div
                                    key={`${result.latitude},${result.longitude}`}
                                    className="px-3 py-2 border-bottom cp"
                                    onClick={() => pickResult(result)}
                                >
                                    <div>{result.label}</div>
                                    {/* Two sections of the same street differ only
                                        in their point, so it is shown: without it
                                        the rows are indistinguishable. */}
                                    <div className="text-monospace text-secondary">
                                        {formatCoordinates(result.latitude, result.longitude)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    }

                    {/* Cleared by the map click below, which is exactly the fix
                        the warning asks for. */}
                    {picked?.isStreet &&
                        <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-3">
                            <img
                                src={Icons.info}
                                alt="info"
                                className="mr-2 flex-shrink-0"
                                style={{ width: "24px", height: "24px" }}
                            />
                            <span>{t("location_coordinates_street_only_warning")}</span>
                        </div>
                    }

                    <CoordinatesPickerMap
                        picked={picked}
                        onPick={(pickedLatitude, pickedLongitude) => setPicked(previous => ({
                            latitude: pickedLatitude,
                            longitude: pickedLongitude,
                            // The address survives the click: nudging the pin onto
                            // the right doorway is the normal way to finish, and
                            // it describes the same place.
                            address: previous?.address || "",
                            focus: false,
                        }))}
                    />

                    <div className="mt-3">
                        <div className="text-secondary mb-1">
                            {t("location_coordinates_address_label")}
                        </div>
                        {/* Says out loud what the field is for: on its own it
                            reads as a duplicate of the search box. */}
                        <div className="text-secondary mb-1">
                            <small>{t("location_coordinates_address_help")}</small>
                        </div>
                        <input
                            className="w-100 px-2 py-1 rounded border"
                            value={picked?.address || ""}
                            maxLength={ADDRESS_MAX_LENGTH}
                            placeholder={t("location_coordinates_address_placeholder")}
                            disabled={!picked}
                            onChange={event => setPicked(previous => previous && {
                                ...previous,
                                address: event.target.value,
                                focus: false,
                            })}
                        />
                    </div>

                    <div className="mt-2 d-flex align-items-center justify-content-between">
                        <span className="text-secondary flex-shrink-0 mr-3">
                            {t("location_coordinates_title")}
                        </span>
                        <span className={picked ? "text-monospace" : "text-secondary"}>
                            {picked
                                ? formatCoordinates(picked.latitude, picked.longitude)
                                : t("location_coordinates_not_set")
                            }
                        </span>
                    </div>
                </div>
            }
            footer={
                <>
                    <CapsuleButton
                        text={t("global_cancel")}
                        // eslint-disable-next-line react/style-prop-object
                        style="INFO"
                        onClick={onClose}
                    />
                    <CapsuleButton
                        text={t("global_save")}
                        // eslint-disable-next-line react/style-prop-object
                        style="PRIMARY"
                        onClick={handleSave}
                        isEnabled={!!isEdited}
                    />
                </>
            }
        />
    );
}

function CoordinatesPickerMap({ picked, onPick }) {

    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    // The map is created once, so its click handler must not close over a stale
    // callback: read it through a ref instead of re-binding on every render.
    const onPickRef = useRef(onPick);
    onPickRef.current = onPick;

    // INITIALIZATION — create the map once, on the initial view (the picked point
    // when there is one, a wide view otherwise).
    useEffect(() => {
        if (mapRef.current || !containerRef.current)
            return;

        const map = L.map(containerRef.current, {
            center: picked
                ? [picked.latitude, picked.longitude]
                : [DEFAULT_VIEW.latitude, DEFAULT_VIEW.longitude],
            zoom: picked ? PICKED_ZOOM : DEFAULT_VIEW.zoom,
            layers: [
                L.tileLayer(
                    "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                    { attribution: '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors' },
                ),
            ],
        });
        // wrap(): panning past the antimeridian keeps counting (a click can come
        // back as lng -190), and the backend stores whatever it is sent, so the
        // longitude is folded into ±180 before it leaves the map.
        map.on("click", event => {
            const { lat, lng } = event.latlng.wrap();
            onPickRef.current(lat, lng);
        });
        mapRef.current = map;

        // The modal is still animating in when this runs, so the container has no
        // final size yet and the tiles would render clipped.
        const resizeTimer = setTimeout(() => map.invalidateSize(), 300);

        return () => {
            clearTimeout(resizeTimer);
            map.remove();
            mapRef.current = null;
            markerRef.current = null;
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Follow the picked point: every pick moves the marker, and a searched one
    // also flies the view to it.
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !picked)
            return;

        const position = [picked.latitude, picked.longitude];

        if (markerRef.current)
            markerRef.current.setLatLng(position);
        else
            markerRef.current = L.circleMarker(position, {
                color: "#009fdf",
                fillColor: "#009fdf",
                fillOpacity: 0.5,
                radius: 8,
                weight: 2,
                interactive: false,
            }).addTo(map);

        // Not conditioned on the point being off-screen: at the wide default view
        // a whole city fits, so a match "already visible" is still an unreadable
        // dot the user cannot check before saving.
        if (picked.focus)
            map.setView(position, Math.max(map.getZoom(), PICKED_ZOOM));
    }, [picked]);

    return (
        <div className="position-relative w-100 c-ratio-16-9 c-rounded-big overflow-hidden border">
            <div
                ref={containerRef}
                className="position-absolute w-100 h-100"
            />
        </div>
    );
}
